import React, { PureComponent, createRef } from 'react';
import { connect } from 'unistore/react';
import styled from 'styled-components';
import { withSize } from 'react-sizeme';

import { scaleLinear } from 'd3';

import actions from '~/state/Actions';
import { media } from '~/styles/Utils';
import theme from '~/styles/Theme';
import { overallAboveSelector, compareAboveSelector } from '~/state/Selectors';

import AreaChart from './AreaChart';
import YAxis from './YAxis';
import IncomeLine from './IncomeLine';
import GridLines from './GridLines';
import ChartLabel from './ChartLabel';
import Tooltip from '../Tooltip';

const Wrapper = styled.div`
  display: flex;
  justify-content: space-between;
  position: relative;
`;

const Svg = styled.svg`
  display: block;
  overflow: visible;
`;

const AxisLabel = styled(ChartLabel)`
  text-anchor: ${(p) => p.textAnchor || 'middle'};
`;

const ChartBgLabel = styled(ChartLabel)`
  font-size: 14px;
`;

const BigNumber = styled.div`
  font-size: 18px;
  font-weight: bold;
  text-shadow: 0px 0px 3px rgba(255, 255, 255, 1);
  text-align: center;
  line-height: 1;

  ${media.m`
    text-align: center;
    font-size: 20px;
  `}
`;

const BgLabelContainer = styled.div`
  position: absolute;
  top: ${(p) => (p.top ? '40px' : 'auto')};
  right: ${(p) => (p.right ? '0' : 'auto')};
  left: ${(p) => (p.left ? '0' : 'auto')};
  bottom: ${(p) => (p.bottom ? '50px' : 'auto')};
  text-align: ${(p) => (p.right ? 'right' : 'left')};
  padding: 3px 4px;
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid ${(p) => p.color || '#777'};
  border-radius: 4px;

  ${BigNumber} {
    color: ${(p) => p.color || '#777'};
  }

  ${media.l`
    right: ${(p) => (p.right ? '15px' : 'auto')};
    left: ${(p) => (p.left ? '15px' : 'auto')};
    bottom: ${(p) => (p.bottom ? '50px' : 'auto')};
  `}
`;

const BgLabel = styled.div`
  font-size: 14px;
  text-shadow: 0px 0px 3px rgba(255, 255, 255, 1);
`;

class Visualisation extends PureComponent {
  containerRef = createRef();

  static defaultProps = {
    margin: {
      top: 40,
      right: 0,
      bottom: 40,
      left: 0,
      middle: 60,
    },
  };

  handleMouseMove(evt) {
    if (!this.containerRef.current) {
      return false;
    }

    const { top, left } = this.containerRef.current.getBoundingClientRect();
    const x = evt.clientX - left;
    const y = evt.clientY - top;

    this.props.setTooltipPosition(x, y);
  }

  render() {
    const {
      data,
      size,
      overallKey,
      compareKey,
      margin,
      overallAbove,
      compareAbove,
    } = this.props;

    if (!data) {
      return null;
    }

    const aspectRatio = window.innerWidth < 768 ? 1 : 0.7;
    const { width } = size;
    const height = width * aspectRatio;

    const chartWidth = (width - margin.middle) / 2;
    const xScaleOverall = scaleLinear()
      .domain([25, 0])
      .range([0, chartWidth])
      .clamp(true)
      .nice();
    const xScaleCompare = scaleLinear()
      .domain([0, 25])
      .range([0, chartWidth])
      .clamp(true)
      .nice();
    const yScale = scaleLinear()
      .domain([0, 7000])
      .range([height, 0])
      .clamp(true);
    const svgHeight = height + margin.top + margin.bottom;

    return (
      <Wrapper
        ref={this.containerRef}
        onMouseMove={(evt) => this.handleMouseMove(evt)}
      >
        <Tooltip />
        <BgLabelContainer top left color={theme.colors.yellow}>
          <BigNumber>{overallAbove}</BigNumber>
          <BgLabel>Prozent sind reicher</BgLabel>
        </BgLabelContainer>

        <BgLabelContainer bottom left color={theme.colors.sand}>
          <BigNumber>{100 - overallAbove}</BigNumber>
          <BgLabel>Prozent sind ärmer</BgLabel>
        </BgLabelContainer>

        <BgLabelContainer bottom right color={theme.colors.lightblue}>
          <BigNumber>{100 - compareAbove}</BigNumber>
          <BgLabel>Prozent sind ärmer</BgLabel>
        </BgLabelContainer>

        <BgLabelContainer top right color={theme.colors.grayblue}>
          <BigNumber>{compareAbove}</BigNumber>
          <BgLabel>Prozent sind reicher</BgLabel>
        </BgLabelContainer>

        <Svg width={width} height={svgHeight}>
          <g transform={`translate(${margin.left}, ${margin.top})`}>
            <GridLines width={width} yScale={yScale} />
            <AreaChart
              xScale={xScaleOverall}
              yScale={yScale}
              width={chartWidth}
              height={height}
              data={data}
              dataKey={overallKey}
              id="area_left"
              aboveColor={theme.colors.yellow}
              aboveHoverColor={theme.colors.yellow}
              belowColor={theme.colors.sand}
              belowHoverColor={theme.colors.yellow}
            />
            <g transform={`translate(${chartWidth + margin.middle}, 0)`}>
              <AreaChart
                xScale={xScaleCompare}
                yScale={yScale}
                width={chartWidth}
                height={height}
                data={data}
                dataKey={compareKey}
                id="area_right"
                aboveColor={theme.colors.grayblue}
                aboveHoverColor={theme.colors.grayblue}
                belowColor={theme.colors.lightblue}
                belowHoverColor={theme.colors.grayblue}
              />
            </g>
            <g transform={`translate(${chartWidth + margin.middle / 2}, 0)`}>
              <YAxis yScale={yScale} />
            </g>
            <IncomeLine yScale={yScale} width={width} />

            <AxisLabel x={width / 2} y={-margin.top + 10}>
              bedarfsgewichtetes Nettoeinkommen
            </AxisLabel>
            <AxisLabel x={width / 2} y={-margin.top + 10}>
              bedarfsgewichtetes Nettoeinkommen
            </AxisLabel>

            <AxisLabel x={width / 4} y={height + 20} textAnchor="middle">
              Häufigkeit der Einkommen
            </AxisLabel>
            <AxisLabel x={(width / 4) * 3} y={height + 20} textAnchor="middle">
              Häufigkeit der Einkommen
            </AxisLabel>
          </g>
        </Svg>
      </Wrapper>
    );
  }
}

export default withSize()(
  connect(
    (state) => ({
      data: state.distributionData,
      overallKey: state.overallKey,
      compareKey: state.compareKey,
      overallAbove: overallAboveSelector(state),
      compareAbove: compareAboveSelector(state),
    }),
    actions
  )(Visualisation)
);
